// Shared utils & constants for Calendar V3 sub-components.
// Extracted from CalendarV3.jsx as part of tech-debt refactor.
//
// COLOUR SYSTEM (finalised Feb 2026):
//   OPEN              → Blue
//   PARTIALLY_FILLED  → Amber
//   ASSIGNED          → Green (emerald)
//   CONFIRMED         → Green (emerald)
//   IN_PROGRESS       → Purple (violet)
//   COMPLETED         → Grey (slate)
//   INVOICED          → Teal
//   CANCELLED         → Stone (kept neutral — red is reserved for NO_SHOW)
//   NO_SHOW           → Red
//   DRAFT             → Slate (fallback)
// No logic changed — purely visual.

// client#4: one distinct colour per status (Assigned/Confirmed were the same green; Draft/Completed/Cancelled three
// near-identical greys). Needs-attention states are warm (orange / yellow / red), active states blue → green,
// finished states neutral (slate / purple). Draft is dashed; Cancelled is faded with the time struck through.
export const STATUS_COLORS = {
  DRAFT:            { bg: 'bg-white border-slate-300 border-dashed', text: 'text-slate-600',             dot: 'bg-slate-300' },
  OPEN:             { bg: 'bg-orange-50 border-orange-400',          text: 'text-orange-800',            dot: 'bg-orange-500' },
  PARTIALLY_FILLED: { bg: 'bg-yellow-50 border-yellow-400',          text: 'text-yellow-800',            dot: 'bg-yellow-500' },
  ASSIGNED:         { bg: 'bg-blue-50 border-blue-400',              text: 'text-blue-800',              dot: 'bg-blue-500' },
  CONFIRMED:        { bg: 'bg-blue-50 border-blue-400',              text: 'text-blue-800',              dot: 'bg-blue-500' },   // legacy — shown as Assigned
  IN_PROGRESS:      { bg: 'bg-green-50 border-green-500',            text: 'text-green-800',             dot: 'bg-green-500' },
  COMPLETED:        { bg: 'bg-slate-100 border-slate-400',           text: 'text-slate-700',             dot: 'bg-slate-600' },
  INVOICED:         { bg: 'bg-purple-50 border-purple-400',          text: 'text-purple-800',            dot: 'bg-purple-600' },
  CANCELLED:        { bg: 'bg-gray-50 border-gray-300',              text: 'text-gray-400 line-through', dot: 'bg-gray-300' },
  NO_SHOW:          { bg: 'bg-red-50 border-red-500',                text: 'text-red-800',               dot: 'bg-red-600' },
};

// Legend order + plain-English meaning (CONFIRMED is legacy — never produced by the backend — so not listed).
export const STATUS_LEGEND = [
  { status: 'DRAFT', label: 'Draft', hint: 'Not published' },
  { status: 'OPEN', label: 'Open', hint: 'Needs a worker' },
  { status: 'PARTIALLY_FILLED', label: 'Partly filled', hint: 'Needs another worker' },
  { status: 'ASSIGNED', label: 'Assigned', hint: 'Worker booked' },
  { status: 'IN_PROGRESS', label: 'In progress', hint: 'Clocked in' },
  { status: 'COMPLETED', label: 'Completed', hint: 'Clocked out' },
  { status: 'INVOICED', label: 'Invoiced', hint: 'Billed' },
  { status: 'CANCELLED', label: 'Cancelled', hint: 'Not going ahead' },
  { status: 'NO_SHOW', label: 'No show', hint: 'Did not happen' },
];

export const getStatusStyle = (s) => STATUS_COLORS[s] || STATUS_COLORS.DRAFT;

// Returns shift duration in hours (min 0). client#13: an overnight shift (22:00–06:00) is 8 h, not 0.
export function shiftDurationHours(shift) {
  if (!shift.start_time || !shift.end_time) return 0;
  const [sh, sm] = shift.start_time.split(':').map(Number);
  const [eh, em] = shift.end_time.split(':').map(Number);
  let mins = (eh * 60 + (em || 0)) - (sh * 60 + (sm || 0));
  if (mins <= 0) mins += 24 * 60;
  return Math.max(0, mins / 60);
}

/** client#13: scheduled hours / shifts per assigned worker whose name matches the search (same rule as the board
 *  API's staff_hours): cancelled shifts don't count; a 2:1 shift counts for each worker. */
export function staffHoursFromShifts(shifts, search) {
  const q = (search || '').trim().toLowerCase();
  if (!q) return [];
  const out = new Map();
  for (const s of shifts || []) {
    if (s.status === 'CANCELLED') continue;
    for (const a of s.assigned_staff || []) {
      if (!(a.name || '').toLowerCase().includes(q)) continue;
      const row = out.get(a.id) || { staff_id: a.id, name: a.name, hours: 0, shift_count: 0 };
      row.hours = Math.round((row.hours + shiftDurationHours(s)) * 100) / 100;
      row.shift_count += 1;
      out.set(a.id, row);
    }
  }
  return [...out.values()].sort((x, y) => x.name.localeCompare(y.name));
}

// Batch 3C C7 (display-only): an overnight shift is shown on both days it spans. Same rollover order as the backend
// resolver — explicit end_date → crosses_midnight → end <= start infers the next day.
export function overnightEndDate(shift) {
  if (!shift?.date) return null;
  if (shift.end_date) return shift.end_date === shift.date ? null : shift.end_date;
  const toMin = (t) => { const [h, m] = (t || '0:0').split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  if (shift.crosses_midnight === true || toMin(shift.end_time) <= toMin(shift.start_time)) {
    const d = new Date(`${shift.date}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return null;
}

// Shifts that started the day before `dk` and end on `dk`, marked for the continuation card (never draggable).
export function continuationsForDay(shifts, dk) {
  return (shifts || []).filter(s => s.date !== dk && overnightEndDate(s) === dk).map(s => ({ ...s, _continuation: true }));
}
